import React from 'react';
import styled, { css } from 'styled-components';
import { HR, Icon, IconTypes, Label, Text } from 'components';
import { VALID_STATUS_PROCESS_ONBOARDING_EMPLOYEES } from 'config/const';
import { Appearance, lineHeight, theme, toRem } from 'design';
import { IOnboardingEmployee, OnboardingEmployeeStatus } from 'types';
import { formatJobTitle } from 'utils';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: ${toRem(theme.space.lg)};
  border: ${toRem(1)} solid ${theme.color.neutral[300]};
  border-radius: ${toRem(4)};
  background-color: ${theme.color.neutral[50]};
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

const StatusContainer = styled.div<{ appearance?: Appearance }>`
  display: flex;
  flex-direction: row nowrap;
  align-items: center;
  justify-content: center;
  min-width: ${toRem(20)};
  min-height: ${toRem(20)};
  border-radius: 50%;
  background-color: ${theme.color.neutral[300]};
  gap: ${toRem(theme.space.sm)};

  ${({ appearance }) =>
    appearance === Appearance.SUCCESS &&
    css`
      background-color: ${theme.color.success[50]};
      svg {
        fill: ${theme.color.white};
      }
    `}

  ${({ appearance }) =>
    appearance === Appearance.NEUTRAL &&
    css`
      background-color: ${theme.color.neutral[200]};
      svg {
        fill: ${theme.color.neutral[500]};
      }
    `}
`;

const Row = styled.div`
  display: flex;
  flex-direction: row nowrap;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.md)};
`;

const Wrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: ${({ fullWidth }) => (fullWidth ? toRem(500) : toRem(140))};
  gap: ${toRem(theme.space.xs)};

  p {
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: normal;

    &.grey {
      color: ${theme.color.neutral[500]};
    }
  }

  label {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    font-weight: ${theme.font.weight.medium};
    color: ${theme.color.neutral[500]};
    white-space: nowrap;
  }
`;

interface IEmployeeProps {
  employee: IOnboardingEmployee;
  dataCy?: string;
}

interface IStatusIcon {
  icon: IconTypes;
  fill: string;
  height: number;
  width: number;
}

const getStatusIcon = (status: OnboardingEmployeeStatus): IStatusIcon => {
  if (status === OnboardingEmployeeStatus.NEW || status === OnboardingEmployeeStatus.ADD_SITE_ROLE) {
    return { icon: 'Plus', fill: theme.color.white, height: 18, width: 18 };
  }

  if (status === OnboardingEmployeeStatus.LINK) {
    return { icon: 'Link', fill: theme.color.white, height: 14, width: 14 };
  }

  if (status === OnboardingEmployeeStatus.REACTIVATE || status === OnboardingEmployeeStatus.REACTIVATE_SITE) {
    return { icon: 'Retry', fill: theme.color.white, height: 14, width: 14 };
  }

  if (status === OnboardingEmployeeStatus.EXIST) {
    return { icon: 'CheckmarkThick', fill: theme.color.neutral[500], height: 14, width: 14 };
  }

  if (status === OnboardingEmployeeStatus.INELIGIBLE || status === OnboardingEmployeeStatus.CONSTRAINT_VIOLATION || status === OnboardingEmployeeStatus.SKIP) {
    return { icon: 'Caution', fill: theme.color.neutral[400], height: 14, width: 14 };
  }

  return { icon: 'RemoveThick', fill: theme.color.neutral[500], height: 18, width: 18 };
};

export const OnboardingEmployee: React.FC<IEmployeeProps> = ({ employee, dataCy = 'employee-onboarding-employee' }) => {
  const statusIcon = getStatusIcon(employee.onboard.status);

  return (
    <Layout data-cy={dataCy}>
      <Row data-cy={`${dataCy}-row`}>
        <StatusContainer data-cy={`${dataCy}-status-container`} appearance={VALID_STATUS_PROCESS_ONBOARDING_EMPLOYEES.includes(employee.onboard.status) ? Appearance.SUCCESS : Appearance.NEUTRAL}>
          <Icon icon={statusIcon.icon} height={statusIcon.height} width={statusIcon.width} fill={statusIcon.fill} />
        </StatusContainer>
        <Wrapper data-cy={`${dataCy}-wrapper`} fullWidth={true}>
          <Text size="small" fontWeight="semibold" data-cy={`${dataCy}-name`}>
            {employee.firstName} {employee.lastName}
          </Text>
          <Text size="xsmall" className="grey" data-cy={`${dataCy}-email`}>
            {employee.inviteRecipient}
          </Text>
        </Wrapper>
        <Wrapper data-cy={`${dataCy}-employee-id-wrapper`}>
          <Label data-cy={`${dataCy}-employee-id-label`}>Employee ID</Label>
          <Text size="xsmall" data-cy={`${dataCy}-employee-id-value`}>
            {employee.employeeId}
          </Text>
        </Wrapper>
        <Wrapper data-cy={`${dataCy}-job-title-wrapper`}>
          <Label data-cy={`${dataCy}-job-title-label`}>Job Title</Label>
          <Text size="xsmall" color={theme.color.neutral[500]} data-cy={`${dataCy}-job-title-value`}>
            {formatJobTitle(employee.job.title)}
          </Text>
        </Wrapper>
      </Row>
      {employee.onboard.reason && (
        <>
          <HR />
          <Row data-cy={`${dataCy}-reason-row`}>
            <Text size="xsmall" color={theme.color.neutral[500]} data-cy={`${dataCy}-reason-value`}>
              {employee.onboard.reason}
            </Text>
          </Row>
        </>
      )}
    </Layout>
  );
};
